import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBffClient, ApiError } from './client';
import type { Device, DeviceDetail, JoinToken, PendingDevice } from '../types/device';

export function useDevices() {
  const client = useBffClient();
  return useQuery({
    queryKey: ['devices'],
    queryFn: () => client.get<Device[]>('/bff/devices'),
  });
}

export function useDevice(deviceId: string | null) {
  const client = useBffClient();
  return useQuery({
    queryKey: ['devices', deviceId],
    queryFn: () => client.get<DeviceDetail>(`/bff/devices/${deviceId}`),
    enabled: !!deviceId,
  });
}

export function useJoinToken() {
  const client = useBffClient();
  return useQuery({
    queryKey: ['join-token'],
    queryFn: async () => {
      try {
        return await client.get<JoinToken>('/bff/devices/join-token');
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useRevokeJoinToken() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.delete('/bff/devices/join-token'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['join-token'] });
    },
  });
}

export function usePendingDevices() {
  const client = useBffClient();
  return useQuery({
    queryKey: ['devices', 'pending'],
    queryFn: () => client.get<PendingDevice[]>('/bff/devices/pending'),
  });
}

export function useApproveDevice() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) => client.postVoid(`/bff/devices/${deviceId}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

export function useRejectDevice() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) => client.postVoid(`/bff/devices/${deviceId}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', 'pending'] });
    },
  });
}

export function useRevokeDevice() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) => client.postVoid(`/bff/devices/${deviceId}/revoke`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

export function useRemoveDevice() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) => client.delete(`/bff/devices/${deviceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['devices', 'pending'] });
    },
  });
}
